/* plat-kalkylator-manual-input.js — V1.5.0 (2026-05-04)
   Progressive enhancement för plat-kalkylator.js:
   ersätter read-only värde-spannet (.pw-field__val) bredvid varje slider
   med en editerbar <input type="number"> som synkar båda vägar mot slidern.

   Bakgrund (Emil Erner-feedback 2026-05-04):
     "vore det bra om det eventuellt för användarvänligheten finns möjligheten
      att skriva i måtten manuellt om det går?"

   Strategi:
     - Vi rör INTE plat-kalkylator.js — den är komplex och tight kopplad till
       3D/SVG-flödet. Vi kör en MutationObserver som triggas när nya
       .pw-field--slider-rader läggs till av kalkylatorn, och uppgraderar dem.
     - Inputs respekterar slidrens min/max/step (read från attribut).
     - Inputvalidering: blur-event klampar värdet inom [min, max] och stegar
       till närmaste step (om step > 0).
     - Synkning bägge vägar: vi triggar 'input'-event på slidern så att
       befintlig kalkylator-logik (pris, 3D-rebuild, dim-overlay) körs
       oförändrat.

   Tillgänglighet:
     - <input> får aria-label kopierad från slidrens <label>.
     - Touch-targets >= 36px höjd (CSS).
*/

(function () {
  'use strict';

  function clampToStep(value, min, max, step) {
    var v = Number(value);
    if (isNaN(v)) return min;
    if (v < min) v = min;
    if (v > max) v = max;
    if (step && step > 0) {
      v = Math.round((v - min) / step) * step + min;
      // Avoid floating-point drift
      v = Number(v.toFixed(10));
    }
    return v;
  }

  function upgradeSliderRow(row) {
    if (row.dataset.pwManualUpgraded === '1') return;
    var slider = row.querySelector('input[type="range"]');
    var valBox = row.querySelector('.pw-field__val');
    if (!slider || !valBox) return;

    // Identifiera värde-spann och unit-text
    var valueSpan = valBox.querySelector('span'); // <span id="pw-val-XXX">
    if (!valueSpan) return;

    // Plocka enhet (oftast "mm") ur original-textnoden bredvid valueSpan.
    // valBox = "<span>VALUE</span>&nbsp;mm" — vi måste rensa hela boxen
    // (annars hänger "&nbsp;mm" kvar som textnod efter inputen).
    var unitText = valBox.textContent.replace(valueSpan.textContent, '').trim() || 'mm';
    var label = row.querySelector('.pw-field__label');
    var labelText = label ? label.textContent.trim() : 'Mått';

    var min = Number(slider.min);
    var max = Number(slider.max);
    var step = Number(slider.step) || 1;
    var current = Number(slider.value);

    // Bygg ny markup: editerbar input + unit + min/max-hint
    var input = document.createElement('input');
    input.type = 'number';
    input.className = 'pw-field__manual-input';
    input.min = String(min);
    input.max = String(max);
    input.step = String(step);
    input.value = String(current);
    input.setAttribute('aria-label', labelText + ' (manuell inmatning)');
    input.setAttribute('inputmode', 'numeric');

    var unitSpan = document.createElement('span');
    unitSpan.className = 'pw-field__manual-unit';
    unitSpan.textContent = unitText;

    var rangeHint = document.createElement('span');
    rangeHint.className = 'pw-field__manual-range';
    rangeHint.textContent = '(' + min + '–' + max + ' ' + unitText + ')';

    // Rensa valBox helt och bygg om — annars finns "&nbsp;mm" kvar som
    // textnod efter inputen (= dubblerad mm). Behåll dock valueSpan
    // (gömd) eftersom plat-kalkylator.js skriver i den när slidern dras.
    valueSpan.style.display = 'none';
    valBox.classList.add('pw-field__val--manual');
    valBox.innerHTML = '';
    valBox.appendChild(valueSpan);
    valBox.appendChild(input);
    valBox.appendChild(unitSpan);
    valBox.appendChild(rangeHint);

    // INPUT → SLIDER
    var commit = function () {
      var clamped = clampToStep(input.value, min, max, step);
      input.value = String(clamped);
      if (Number(slider.value) !== clamped) {
        slider.value = String(clamped);
        // Trigger så att kalkylatorns 'input'-listener uppdaterar pris + visuellt
        slider.dispatchEvent(new Event('input', { bubbles: true }));
      }
    };

    input.addEventListener('input', function () {
      // Live-update: bara om värdet är inom range, annars vänta tills blur
      var v = Number(input.value);
      if (!isNaN(v) && v >= min && v <= max) {
        if (Number(slider.value) !== v) {
          slider.value = String(v);
          slider.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
    });
    input.addEventListener('blur', commit);
    input.addEventListener('change', commit);
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        commit();
        input.blur();
      }
    });

    // SLIDER → INPUT (när användare drar slidern)
    slider.addEventListener('input', function () {
      input.value = slider.value;
    });

    row.dataset.pwManualUpgraded = '1';
  }

  function upgradeAllInRoot(root) {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    var rows = root.querySelectorAll('.pw-field--slider');
    Array.prototype.forEach.call(rows, upgradeSliderRow);
  }

  function init() {
    var root = document.getElementById('plat-kalkylator');
    if (!root) return;

    // Kör direkt om kalkylatorn redan rendrerade (defer-skript kan ha hunnit före oss)
    upgradeAllInRoot(root);

    // Observera framtida tillägg (kalkylator.js renderar asynkront efter fetch)
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        Array.prototype.forEach.call(m.addedNodes, function (node) {
          if (node.nodeType !== 1) return;
          if (node.classList && node.classList.contains('pw-field--slider')) {
            upgradeSliderRow(node);
          } else {
            upgradeAllInRoot(node);
          }
        });
      });
    });
    observer.observe(root, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();